import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectIncomeUnitSummaries, selectMembers } from '@/utils/store';
import { formatCurrency } from '@/utils/format';
import AddIncomeUnitModal from '@/components/shared/AddIncomeUnitModal';
import AddTransactionModal from '@/components/shared/AddTransactionModal';
import InfoTooltip from '@/components/ui/InfoTooltip';
import { Plus, Trash2, TrendingUp, Layers, Copy, Repeat, CircleDot, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

function formatMonthLabel(monthStr, locale) {
  const [y, m] = monthStr.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString(locale === 'es' ? 'es-CL' : 'en-US', { month: 'long', year: 'numeric' });
}

function shiftMonth(monthStr, delta) {
  const [y, m] = monthStr.split('-').map(Number);
  const date = new Date(y, m - 1 + delta);
  return date.toISOString().slice(0, 7);
}

export default function IncomeUnits() {
  const { t, i18n } = useTranslation();
  const unitSummaries = useStore(selectIncomeUnitSummaries);
  const members = useStore(selectMembers);
  const removeIncomeUnit = useStore((s) => s.removeIncomeUnit);
  const duplicateIncomeUnit = useStore((s) => s.duplicateIncomeUnit);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const openEdit = (unit) => {
    setEditingUnit(unit);
    setShowUnitModal(true);
  };

  const closeUnitModal = () => {
    setShowUnitModal(false);
    setEditingUnit(null);
  };

  const handleDuplicate = (e, unitId) => {
    e.stopPropagation();
    const nextMonth = shiftMonth(selectedMonth, 1);
    duplicateIncomeUnit(unitId, nextMonth);
    setSelectedMonth(nextMonth);
  };

  // Filter by selected month
  const filtered = useMemo(() => {
    return unitSummaries.filter(({ unit }) => unit.month === selectedMonth);
  }, [unitSummaries, selectedMonth]);

  // Available months for navigation
  const allMonths = useMemo(() => {
    const set = new Set(unitSummaries.map(({ unit }) => unit.month).filter(Boolean));
    set.add(selectedMonth);
    return [...set].sort();
  }, [unitSummaries, selectedMonth]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white">
          {t('incomeUnits.title')}
        </h1>
        <InfoTooltip text={t('common.hints.incomeUnits')} size={16} />
      </div>

      {/* Two action buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowIncomeModal(true)}
          className="flex-1 py-3 rounded-xl text-sm font-semibold bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 hover:bg-success-100 dark:hover:bg-success-500/20 transition-colors flex items-center justify-center gap-2"
        >
          <TrendingUp size={16} />
          + {t('incomeUnits.simpleIncome')}
        </button>
        <button
          onClick={() => setShowUnitModal(true)}
          className="flex-1 py-3 rounded-xl text-sm font-semibold bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors flex items-center justify-center gap-2"
        >
          <Layers size={16} />
          + {t('incomeUnits.addNew')}
        </button>
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setSelectedMonth(shiftMonth(selectedMonth, -1))}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
          {formatMonthLabel(selectedMonth, i18n.language)}
        </span>
        <button
          onClick={() => setSelectedMonth(shiftMonth(selectedMonth, 1))}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Unit cards */}
      {filtered.length > 0 ? (
        <div className="space-y-4">
          {filtered.map(({ unit, totalFixed, totalVariable, netIncome }) => {
            const ratio = unit.grossIncome > 0 ? Math.round((netIncome / unit.grossIncome) * 100) : 0;
            const unitMembers = unit.memberIds?.length > 0 ? unit.memberIds.map((id) => members.find((m) => m.id === id)).filter(Boolean) : [];
            return (
              <div
                key={unit.id}
                className="card hover:border-brand-300 dark:hover:border-brand-600 transition-colors cursor-pointer group"
                onClick={() => openEdit(unit)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                      style={{ backgroundColor: unit.color + '20' }}
                    >
                      {unit.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{unit.name}</h3>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          unit.recurrence === 'one_time'
                            ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                            : 'bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400'
                        }`}>
                          {unit.recurrence === 'weekly' ? <CalendarDays size={10} /> : unit.recurrence === 'monthly' ? <Repeat size={10} /> : <CircleDot size={10} />}
                          {t(`incomeUnits.${unit.recurrence === 'one_time' ? 'oneTime' : unit.recurrence || 'monthly'}`)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {unit.currency}
                        {unitMembers.length > 0 ? ` · ${unitMembers.map((m) => m.name).join(', ')}` : ` · ${t('incomeUnits.shared')}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => handleDuplicate(e, unit.id)}
                      className="p-1.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-brand-500 dark:hover:text-brand-400 transition-all"
                      title={t('incomeUnits.duplicate')}
                    >
                      <Copy size={15} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeIncomeUnit(unit.id); }}
                      className="p-1.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-danger-500 dark:hover:text-danger-400 transition-all"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* Amounts */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('incomeUnits.grossIncome')}
                      <InfoTooltip text={t('common.hints.income')} size={12} />
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(unit.grossIncome, unit.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('incomeUnits.fixedCosts')}
                      <InfoTooltip text={t('common.hints.fixedCosts')} size={12} />
                    </div>
                    <div className="text-sm font-semibold text-danger-600 dark:text-danger-400">
                      -{formatCurrency(totalFixed, unit.currency)}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('incomeUnits.variableCosts')}
                      <InfoTooltip text={t('common.hints.variableCosts')} size={12} />
                    </div>
                    <div className="text-sm font-semibold text-danger-600 dark:text-danger-400">
                      -{formatCurrency(totalVariable, unit.currency)}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all ${
                        ratio > 70 ? 'bg-success-500' : ratio > 50 ? 'bg-warning-500' : 'bg-danger-500'
                      }`}
                      style={{ width: `${Math.max(0, Math.min(100, ratio))}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold min-w-fit ${
                    ratio > 70 ? 'text-success-600 dark:text-success-400' : ratio > 50 ? 'text-warning-600 dark:text-warning-400' : 'text-danger-600 dark:text-danger-400'
                  }`}>
                    {formatCurrency(netIncome, unit.currency)}
                  </span>
                  <span className="text-xs text-gray-400 min-w-[3ch] text-right">{ratio}%</span>
                </div>

                {/* Fixed costs breakdown */}
                {unit.fixedCosts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex flex-wrap gap-2">
                      {unit.fixedCosts.map((cost) => (
                        <span
                          key={cost.id}
                          className="text-xs px-2 py-1 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                        >
                          {cost.name}: -{formatCurrency(cost.amount, unit.currency)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3">💰</div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('incomeUnits.noUnitsMonth')}
          </p>
          <button
            onClick={() => setShowUnitModal(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
          >
            <Plus size={16} />
            {t('incomeUnits.addNew')}
          </button>
        </div>
      )}

      <AddIncomeUnitModal open={showUnitModal} onClose={closeUnitModal} unit={editingUnit} />
      <AddTransactionModal
        open={showIncomeModal}
        onClose={() => setShowIncomeModal(false)}
        defaultType="income"
      />
    </div>
  );
}
